import { useState } from 'react';
import { CHARACTERS } from './gameConstants';

export const PlayerDeadOverlay = ({ onQuit }) => {
  return (
    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3" data-testid="player-dead-overlay">
      <div className="bg-black/60 backdrop-blur-sm rounded-2xl px-4 py-2 flex items-center gap-3">
        <span className="text-white/70 text-xs" style={{ fontFamily: 'Nunito, sans-serif' }}>
          You're out! Watching...
        </span>
        <button
          onClick={onQuit}
          className="px-4 py-1.5 rounded-xl text-xs font-bold text-white transition-all hover:scale-105 active:scale-95"
          style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', fontFamily: 'Fredoka, sans-serif', boxShadow: '0 2px 8px rgba(239,68,68,0.4)' }}
          data-testid="quit-game-button"
        >
          Quit
        </button>
      </div>
    </div>
  );
};

export const RankingsScreen = ({ rankings, playerWon, onDone, isRoomMode, onPlayAgain, onGoHome, roomReadyCount, roomTotalCount }) => {
  const medals = ['🥇', '🥈', '🥉', '4th'];
  const [pressedPlayAgain, setPressedPlayAgain] = useState(false);

  const handlePlayAgain = () => {
    setPressedPlayAgain(true);
    if (onPlayAgain) onPlayAgain();
  };

  return (
    <div
      className="absolute inset-0 z-[60] flex items-center justify-center"
      data-testid="rankings-screen"
      style={{ background: playerWon ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : 'linear-gradient(135deg, #1e293b, #334155)' }}
    >
      <div className="w-full max-w-xs mx-4 text-center">
        {/* Title */}
        {playerWon ? (
          <>
            <div className="text-5xl mb-2" style={{ animation: 'characterFloat 1.5s ease-in-out infinite' }}>🏆</div>
            <h2 className="text-4xl font-bold mb-1"
              style={{ fontFamily: 'Fredoka, sans-serif', color: '#d97706', textShadow: '2px 2px 0 #92400e' }}>
              You Won!
            </h2>
            <p className="text-amber-800/60 text-sm mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Incredible flight!
            </p>
          </>
        ) : (
          <>
            <div className="text-4xl mb-2">🏁</div>
            <h2 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Race Over!
            </h2>
            <p className="text-white/50 text-sm mb-5" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Final standings
            </p>
          </>
        )}

        {/* Rankings list */}
        <div className="space-y-2 mb-6">
          {rankings.map((entry, i) => (
            <div key={i}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all ${
                entry.isPlayer
                  ? playerWon ? 'bg-amber-400/30 border-2 border-amber-500' : 'bg-white/15 border-2 border-white/30'
                  : playerWon ? 'bg-white/20' : 'bg-white/5'
              }`}
              data-testid={`ranking-${i + 1}`}
            >
              <span className="text-2xl w-8 text-center">{medals[i] || `${i + 1}`}</span>
              {entry.character && (() => {
                const charData = CHARACTERS.find(c => c.id === (entry.character.id || entry.character)) || entry.character;
                return (
                  <div className="w-10 h-10 flex-shrink-0">
                    <img src={charData.png || charData.gif} alt="" className="w-full h-full object-contain" />
                  </div>
                );
              })()}
              <div className="flex-1 text-left min-w-0">
                <p className={`font-bold text-sm truncate ${playerWon ? 'text-slate-800' : 'text-white'}`}
                  style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {entry.name} {entry.isPlayer && '(You)'}
                </p>
              </div>
              <div className="flex flex-col items-end">
                <span className={`text-lg font-bold ${playerWon ? 'text-amber-700' : 'text-amber-400'}`}
                  style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid={`ranking-${i + 1}-score`}>
                  {entry.score}
                </span>
                {entry.pipes != null && (
                  <span className={`text-[10px] ${playerWon ? 'text-amber-800/50' : 'text-white/40'}`}
                    style={{ fontFamily: 'Nunito, sans-serif' }} data-testid={`ranking-${i + 1}-breakdown`}>
                    {entry.pipes} pipes + {entry.coins || 0} coins
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Action buttons */}
        {isRoomMode ? (
          <div className="space-y-2.5">
            <button
              onClick={handlePlayAgain}
              disabled={pressedPlayAgain}
              className={`w-full py-4 text-white text-xl font-bold rounded-2xl transition-all ${
                pressedPlayAgain ? 'opacity-60 cursor-not-allowed' : 'hover:scale-105 active:scale-95'
              }`}
              style={{
                fontFamily: 'Fredoka, sans-serif',
                background: pressedPlayAgain
                  ? 'linear-gradient(135deg, #6b7280, #4b5563)'
                  : 'linear-gradient(135deg, #22c55e, #16a34a)',
                boxShadow: pressedPlayAgain ? '0 5px 0 #374151' : '0 5px 0 #15803d',
              }}
              data-testid="rankings-play-again"
            >
              {pressedPlayAgain ? 'Waiting...' : 'Play Again'}
            </button>
            {(roomReadyCount != null && roomReadyCount > 0) && (
              <p className="text-white/40 text-[11px] text-center" style={{ fontFamily: 'Nunito, sans-serif' }} data-testid="room-ready-status">
                {roomReadyCount}/{roomTotalCount} ready — game starts when everyone presses Play Again
              </p>
            )}
            <button
              onClick={onGoHome}
              className="w-full py-3 text-sm font-bold rounded-2xl transition-all hover:scale-105 active:scale-95"
              style={{
                fontFamily: 'Fredoka, sans-serif',
                background: 'rgba(255,255,255,0.1)',
                color: playerWon ? '#92400e' : 'rgba(255,255,255,0.6)',
                border: playerWon ? '1px solid rgba(146,64,14,0.2)' : '1px solid rgba(255,255,255,0.1)',
              }}
              data-testid="rankings-go-home"
            >
              Home
            </button>
          </div>
        ) : (
          <button
            onClick={onDone}
            className="w-full py-4 text-white text-xl font-bold rounded-2xl transition-all hover:scale-105 active:scale-95"
            style={{
              fontFamily: 'Fredoka, sans-serif',
              background: playerWon ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #38bdf8, #0284c7)',
              boxShadow: playerWon ? '0 5px 0 #92400e' : '0 5px 0 #0369a1',
            }}
            data-testid="rankings-done"
          >
            Continue
          </button>
        )}
      </div>
    </div>
  );
};
